import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Copy, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `/api`;

export default function CheckIn() {
  const navigate = useNavigate();
  const [step, setStep] = useState("form"); // form | success
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({ name: "", contact: "", type: "" });
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.contact.trim() || !form.type) {
      toast.error("All fields are required");
      return;
    }
    if (!/^\d{10}$/.test(form.contact)) {
      toast.error("Contact number must be exactly 10 digits");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/entry`, form);
      setResult(res.data);
      setStep("success");
    } catch (err) {
      const msg = err.response?.data?.detail || "Check-in failed. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const copyEventId = async () => {
    if (!result?.event_id) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(result.event_id);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = result.event_id;
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopied(true);
      toast.success("Event ID copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.info(`Your Event ID: ${result.event_id}`);
    }
  };

  if (step === "success" && result) {
    return (
      <div className="min-h-screen flex flex-col px-6 py-8 max-w-lg mx-auto w-full">
        <div className="flex-1 flex flex-col justify-center">
          {/* Success indicator */}
          <div className="animate-fade-in mb-8">
            <div className="w-16 h-16 bg-[#002FA7] flex items-center justify-center mb-6">
              <Check className="w-8 h-8 text-white" />
            </div>
            <p className="overline text-[#002FA7] mb-3" data-testid="success-overline">
              Checked In
            </p>
            <h1
              className="text-3xl sm:text-4xl font-black tracking-tight text-[#0A0A0A] leading-tight mb-2"
              data-testid="success-welcome"
            >
              Welcome, {result.name}
            </h1>
            <p className="text-sm text-[#525252]">
              You're now registered for the event
            </p>
          </div>

          {/* Event ID Card */}
          <div
            className="swiss-card p-6 mb-6 animate-fade-in animate-delay-2"
            data-testid="event-id-card"
          >
            <p className="overline text-[#525252] mb-4">Your Event ID</p>
            <p className="event-id-display mb-4" data-testid="event-id-display">
              {result.event_id}
            </p>
            <button
              onClick={copyEventId}
              className="flex items-center gap-2 text-sm font-medium text-[#002FA7] hover:text-[#001e6e] transition-colors"
              data-testid="copy-event-id-btn"
            >
              {copied ? (
                <><Check className="w-4 h-4" /> Copied</>
              ) : (
                <><Copy className="w-4 h-4" /> Copy to clipboard</>
              )}
            </button>
          </div>

          {/* Important note */}
          <div
            className="border border-[#FF3333]/30 bg-[#FF3333]/5 p-4 animate-fade-in animate-delay-3"
            data-testid="save-id-notice"
          >
            <p className="text-sm font-semibold text-[#0A0A0A] mb-1">Save this ID</p>
            <p className="text-xs text-[#525252] leading-relaxed">
              You'll need this Event ID when checking out. Take a screenshot or note it down.
            </p>
          </div>
        </div>

        {/* Bottom action */}
        <div className="pt-6 animate-fade-in animate-delay-4">
          <Button
            onClick={() => navigate("/")}
            className="w-full h-12 btn-brand rounded-none text-sm"
            data-testid="back-to-home-btn"
          >
            Done
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col px-6 py-8 max-w-lg mx-auto w-full">
      {/* Header */}
      <div className="mb-8 animate-fade-in">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-sm text-[#525252] hover:text-[#0A0A0A] transition-colors mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <p className="overline text-[#002FA7] mb-3">Check In</p>
        <div className="mb-4">
          <img
            src="/EO Odisha Logo.png"
            alt="EO Odisha Logo"
            className="h-16 w-auto object-contain"
          />
        </div>
        <p className="text-sm text-[#525252] mt-2">
          Register your attendance at the event
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
        <div className="space-y-5 animate-fade-in animate-delay-1">
          <div>
            <Label htmlFor="name" className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-2 block">
              Full Name
            </Label>
            <Input
              id="name"
              type="text"
              placeholder="Enter your full name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="h-12 rounded-none border-black/20 bg-transparent focus-visible:ring-2 focus-visible:ring-[#002FA7] focus-visible:ring-offset-2 text-base"
              data-testid="checkin-name-input"
            />
          </div>

          <div>
            <Label htmlFor="contact" className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-2 block">
              Contact Number
            </Label>
            <Input
              id="contact"
              type="tel"
              maxLength="10"
              placeholder="Enter your 10-digit phone number"
              value={form.contact}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                if (value.length <= 10) setForm({ ...form, contact: value });
              }}
              className="h-12 rounded-none border-black/20 bg-transparent focus-visible:ring-2 focus-visible:ring-[#002FA7] focus-visible:ring-offset-2 text-base"
              data-testid="checkin-contact-input"
            />
          </div>

          <div>
            <Label htmlFor="type" className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-2 block">
              Membership Type
            </Label>
            <Select
              value={form.type}
              onValueChange={(value) => setForm({ ...form, type: value })}
            >
              <SelectTrigger
                id="type"
                className="h-12 rounded-none border-black/20 bg-transparent focus-visible:ring-2 focus-visible:ring-[#002FA7] focus:ring-offset-2 text-base"
                data-testid="checkin-type-select"
              >
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent className="rounded-none border-black/20">
                <SelectItem value="Member">Member</SelectItem>
                <SelectItem value="SLP">SLP</SelectItem>
                <SelectItem value="MEMBER + SLP">MEMBER + SLP</SelectItem>
                <SelectItem value="GUEST">GUEST</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Submit - moved here */}
          <div className="pt-4">
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 btn-brand rounded-none text-sm"
              data-testid="checkin-submit-btn"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Checking in...</>
              ) : (
                "Check In"
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
